import { User } from "@easycv/common/types/user";
import dayjs from "dayjs";
import {
  getDate,
  getPeriodFromDateObject,
  getStringFromDateObject,
  isTimeWithinRange
} from "../../lib/dateHelpers.js";
import { getEffortText } from "../../lib/formatUtilities.js";
import { getLastPosition } from "../../lib/reportUtilities.js";
import { CCInteractiveTable } from "../cclib/CCInteractiveTable";
import { CCManage } from "../cclib/CCManage";
import { CCSetup } from "../cclib/CCSetup";
import { CCStructure } from "../cclib/CCStructure";
import { CCStyling, DefaultStyling } from "../cclib/CCStyling";
import { CCHeader, CCInput, CCText, CCWrapper } from "../cclib/components/Form";
import { CCButton, CCIcon } from "../cclib/components/UI";
import { InferedRowType } from "../cclib/types/table.js";
import { uclaDataSummaryStructure } from "./structure_ucla_data_summary";
import { uclaDataSummaryStructureProfessor } from "./structure_ucla_data_summary_professor";

// Passed to `getPeriodFromDateObject`. ' - ' is prepended by `getPeriodFromDateObject`
const ONGOING_TEXT = "Present";
const dataPrefix = "ucla_ds_";
const presentationCategories = ["Oral", "Poster", "Lectures"] as const;
const presentationSubcategories = [
  "Local",
  "Regional",
  "National",
  "International"
] as const;

const sortRows = (rows: any[], dateKey: string) => {
  rows.sort((x, y) => {
    const possibleFormats = ["MM/YYYY", "MM/DD/YYYY", "YYYY"];
    const xstr = String(x[dateKey]).toLowerCase().replace(/\s+/g, "");
    const xend = xstr.includes("-") ? xstr.split("-")[1] : xstr;
    const xstart = xstr.split("-")[0];
    const ystr = String(y[dateKey]).toLowerCase().replace(/\s+/g, "");
    const yend = ystr.includes("-") ? ystr.split("-")[1] : ystr;
    const ystart = ystr.split("-")[0];

    if (xend == ONGOING_TEXT.toLowerCase()) {
      if (yend == ONGOING_TEXT.toLowerCase()) {
        let xday = dayjs(xstart, possibleFormats);
        if (!xday?.isValid()) return -1;
        let yday = dayjs(ystart, possibleFormats);
        if (!yday?.isValid()) return 1;
        return xday.isAfter(yday) ? 1 : -1;
      } else {
        // only x is ongoing, x is after y
        return 1;
      }
    } else {
      if (yend == ONGOING_TEXT.toLowerCase()) {
        // only y is ongoing, y is after x
        return -1;
      } else {
        // Both are not ongoing, compare end.
        let xday = dayjs(xend, possibleFormats);
        if (!xday?.isValid()) return -1;
        let yday = dayjs(yend, possibleFormats);
        if (!yday?.isValid()) return 1;
        return xday.isAfter(yday) ? 1 : -1;
      }
    }
  });
};
const hookEditHasher = (table: CCInteractiveTable<any>): void => {
  table.addPseudoColumn(
    /.*/,
    "editHash",
    (_value: any, event: any) => {
      // Grab the row's data
      const data =
        table.cc.newDef.career[table.cc.viewYear].data[
          table.dataPrefix + table.dataKey
        ];
      // If data exists
      if (data && data[event.row]) {
        // Combine all columns into a single string (index)(column's key)(value)(index)(column's key)(value)...
        const str = Object.entries(data[event.row])
          .map(([key, val], index) => {
            if (table.columns.some(({ db }) => db == key)) {
              return String(index + key + val);
            }
            return "";
          })
          .join("");
        return table.quickStringHash(str);
      }
      return null;
    },
    true
  );
};

const parseRoles = (roles: { id: string; role: string; time: object }[]) => {
  if (roles == undefined || roles.filter(Boolean).length == 0) return "";
  return roles.map(({ role }) => role).join(", ");
};

const tableStatus = (data: any, _cc: CCSetup): void => {
  if (data) {
    if (Array.isArray(data)) {
      data.forEach((table: CCInteractiveTable) => {
        table.populateFromDatabase();
      });
    } else {
      (data as CCInteractiveTable).populateFromDatabase();
    }
  }
};

// Check that timeframe has a start and end date (string is not "")
// Returns false for invalid, true for valid
const checkTimeframeValidity = (timeframe: [string, string]): boolean => {
  if (!Boolean(timeframe[0].length) || !Boolean(timeframe[1].length)) {
    window.alert("Invalid timeframe. Please select a start and end date.");
    return false;
  }
  return true;
};
// Get the selected timeframe
const getTimeframeSelection = (): [string, string] => {
  const start = $("input[data-key=timeframeSelectionStart]").val();
  const end = $("input[data-key=timeframeSelectionEnd]").val();
  return [String(start), String(end)];
};

// Filter object for entries that aren't null and within timeframe. Returns keys
const filterTimeframe = (
  obj: object,
  timeframe: [string, string],
  additionalFilter: (item: any) => boolean = () => true
): string[] => {
  return Object.keys(obj).filter((key) => {
    return (
      obj[key] != null &&
      obj[key].time?.start != null &&
      isTimeWithinRange(obj[key].time, ...timeframe) &&
      additionalFilter(obj[key])
    );
  });
};

const getParsedEffort = (
  effort?: { num: number; per: string } | string
): string => {
  return getEffortText({ effort }).replace("/", "per");
};

const timeframeSelectSection = (container: JQuery, _cc: CCSetup): void => {
  const datePickerStyle = CCStyling.fromObject({
    width: "min-content"
  });
  container.append(
    CCWrapper(
      CCHeader(
        "h5",
        "",
        "SELECT THE APPROPRIATE TIMEFRAME TO PRE-FILL DATA FOR THE FOLLOWING SECTIONS FROM YOUR CV"
      ) +
        CCInput(
          "Date",
          "",
          "timeframeSelectionStart",
          "cc-optional",
          datePickerStyle,
          { includeWrapper: false }
        ) +
        '<span style="margin: 0 10px 0 10px">to</span>' +
        CCInput(
          "Date",
          "",
          "timeframeSelectionEnd",
          "cc-optional",
          datePickerStyle,
          { includeWrapper: false }
        ) +
        CCButton(
          "primary",
          "timeframeLoad",
          "LOAD ALL SECTION DATA FROM CV",
          CCStyling.fromObject({ "margin-left": "15px" })
        ),
      "noPrint"
    )
  );
  const timeframeStart = $("input[data-key='timeframeSelectionStart']");
  const timeframeEnd = $("input[data-key='timeframeSelectionEnd']");
  const heldStart = localStorage.getItem("timeframeSelectionStart");
  const heldEnd = localStorage.getItem("timeframeSelectionEnd");
  if (heldStart != null) timeframeStart.val(heldStart);
  if (heldEnd != null) timeframeEnd.val(heldEnd);

  timeframeStart.on("change", (event) => {
    localStorage.setItem(
      "timeframeSelectionStart",
      String($(event.target).val())
    );
  });
  timeframeEnd.on("change", (event) => {
    localStorage.setItem(
      "timeframeSelectionEnd",
      String($(event.target).val())
    );
  });

  if ($('[data-key="appointment_date"]').length) {
    container.append(
      CCWrapper(
        CCHeader(
          "h4",
          "",
          "Information for this form is pulled directly from your CV. Anything created, updated, or deleted on this form is a localized change and does NOT flow back to the original CV record. Please bring your CV up-to-date before completing this form."
        )
      )
    );
  }

  $(".timeframeLoad").on("click", () => {
    // Check that timeframe is valid. If we dont here the user would get spammed with alerts
    if (!checkTimeframeValidity(getTimeframeSelection())) return;
    $(".teachingRecordLoad").trigger("click");
    $(".teachingOtherLoad").trigger("click");
    $(".committeeLoad").trigger("click");
    $(".honorsLoad").trigger("click");
    $(".activitiesLoad").trigger("click");
  });
};

const degreesSection = (container: JQuery, cc: CCSetup) => {
  // Add HTML to the container
  const degreeStyle = CCStyling.fromObject({
    "font-weight": "bold",
    "text-decoration": "underline"
  });
  container.append(
    CCWrapper(
      CCText("h5", "", "DEGREES", degreeStyle) +
        CCButton(
          "primary",
          "populateEducation noPrint",
          "Populate Education Data from CV"
        )
    )
  );
  // Define table columns
  const tableColumns = [
    { display: "Degrees", db: "degree" as const },
    { display: "Date", db: "time" as const },
    { display: "Institution", db: "location" as const }
  ];

  // Create table and populate from database
  const table = new CCInteractiveTable(
    container,
    cc,
    "degrees",
    dataPrefix,
    "custom-degrees",
    tableColumns,
    {
      addRowButton: false,
      removeRowButton: "right",
      editable: {
        disableEverything: false,
        allowAddingRows: false,
        allowEditingText: false,
        allowRemovingRows: true
      }
    }
  );
  table.populateFromDatabase();

  // populateEducation button
  $(".populateEducation").on("click", () => {
    // Clear the table
    table.clear();
    // Get education data from CV
    const education = cc.newDef?.lists?.education || {};
    // Filter out nulls
    const educationKeys = Object.keys(education).filter(
      (key) => education[key] != null && education[key].time?.end?.year != null
    );
    // Add a row for each data point
    const rows: InferedRowType<typeof tableColumns>[] = [];
    educationKeys.forEach((key) => {
      rows.push({
        degree: education[key].degree,
        time: education[key].time.end.year,
        location: education[key].location
      });
    });
    sortRows(rows, "time");
    table.bulkAddRowsFromData(rows, true);
  });

  return table;
};

const teachingRecordSection = (container: JQuery, cc: CCSetup) => {
  // Add some HTML
  container.append(
    CCWrapper(
      CCButton("primary", "teachingRecordClear noPrint", "CLEAR ALL DATA") +
        CCButton("primary", "teachingRecordLoad noPrint", "LOAD DATA FROM CV")
    )
  );
  // Define table columns
  const tableColumns = [
    { display: "TERM-YEAR", db: "term" as const, width: "8%" },
    { display: "COURSE NO. (1)", db: "courseNum" as const, width: "9%" },
    { display: "DIV (Y/N)", db: "div" as const, width: "7%" },
    { display: "TITLE OF COURSE", db: "title" as const, width: "40%" },
    { display: "REQUIRED (2)", db: "required" as const, width: "8%" },
    { display: "ENROLLMENT", db: "attendance" as const, width: "8%" },
    {
      display: "OTHER FACULTY PARTICIPANTS (3)",
      db: "otherFacultyParticipants" as const,
      width: "20%"
    }
  ];
  // Create table and populate from database
  const table = new CCInteractiveTable(
    container,
    cc,
    "teachingRecord",
    dataPrefix,
    "custom-teaching-record",
    tableColumns,
    {
      widthOverride: "1350px",
      editable: {
        allowRemovingRows: false,
        allowEditingText: true,
        allowAddingRows: false,
        disableEverything: false
      }
    }
  );
  table.populateFromDatabase();

  // Teaching record clear button
  $(".teachingRecordClear").on("click", () => table.clear());

  // Teaching record load button
  $(".teachingRecordLoad").on("click", () => {
    // Figure out how many years back we are pulling
    const timeframe = getTimeframeSelection();
    if (!checkTimeframeValidity(timeframe)) return;
    // Clear the table
    table.clearUnedited();
    // Get data from CV
    const teaching = cc?.newDef?.lists?.teaching || {};
    const teachingKeys = filterTimeframe(teaching, timeframe);
    const rows: (InferedRowType<typeof tableColumns> & {
      location: string;
      comments: string;
    })[] = [];
    teachingKeys.forEach((key) => {
      if (Boolean(teaching[key].courseNumber)) {
        rows.push({
          term: getDate(teaching[key], "fullMonthDayYear"),
          courseNum: teaching[key].courseNumber,
          div: teaching[key].dei == true ? "Y" : "N",
          title: teaching[key].name,
          attendance: teaching[key].attendance,
          otherFacultyParticipants: "",
          required: "",
          location: teaching[key].location,
          comments: teaching[key].description
        });
      }
    });
    sortRows(rows, "term");
    for (const row of rows) {
      const key = table.addRowFromData(row).split("-").pop();
      if (Boolean(key) && !isNaN(+key) && isFinite(+key)) {
        table.addHiddenData(+key, "location", row.location);
        table.addHiddenData(+key, "comments", row.comments);
      }
    }

    // Special case - listen for edits so we can take care of the combined column in the output
    table.addPseudoColumn(/^div$/, "isDiv", (value: any) => {
      const normalized = String(value).toUpperCase().trim();
      return normalized === "Y" || normalized === "YES";
    });
  });

  hookEditHasher(table);
  return table;
};
const teachingOtherSection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(
      CCButton("primary", "teachingOtherClear noPrint", "CLEAR ALL DATA") +
        CCButton("primary", "teachingOtherLoad noPrint", "LOAD DATA FROM CV")
    )
  );
  const otherTeachingColumns = [
    { display: "Term-Year", db: "term" as const, width: "14%" },
    { display: "Course Name", db: "title" as const },
    { display: "Location", db: "location" as const },
    { display: "Additional Comments", db: "comments" as const }
  ];
  const otherTeachingTable = new CCInteractiveTable(
    container,
    cc,
    "teachingOther",
    dataPrefix,
    "custom-teaching-record-no-course-num",
    otherTeachingColumns
  );
  // Define mentee columns
  const menteeColumns = [
    { display: "Mentee Name", db: "menteeName" as const },
    { display: "Type of Mentee", db: "menteeType" as const },
    { display: "Academic Outputs", db: "academicOutputs" as const },
    { display: "Effort", db: "effort" as const, width: "15%" }
  ];

  container.append(CCWrapper(CCHeader("h5", "", "Current Mentees")));
  // Create table
  const menteeTable = new CCInteractiveTable(
    container,
    cc,
    "teachingMentees",
    dataPrefix,
    "custom-teaching-mentees",
    menteeColumns
  );

  // Populate from db
  menteeTable.populateFromDatabase();
  otherTeachingTable.populateFromDatabase();

  // Clear button handler
  $(".teachingOtherClear").on("click", () => {
    menteeTable.clear();
    otherTeachingTable.clear();
  });

  // Load button handler
  $(".teachingOtherLoad").on("click", () => {
    // Figure out how many years back we are pulling
    const timeframe = getTimeframeSelection();
    if (!checkTimeframeValidity(timeframe)) return;
    // Clear both tables
    menteeTable.clearUnedited();
    otherTeachingTable.clearUnedited();

    const teaching = cc?.newDef?.lists?.teaching || {};
    const teachingKeys = filterTimeframe(teaching, timeframe);
    const otherTeachingRows: InferedRowType<typeof otherTeachingColumns>[] = [];
    teachingKeys.forEach((key) => {
      if (!Boolean(teaching[key].courseNumber)) {
        otherTeachingRows.push({
          term: getDate(teaching[key], "monthAndFullYear"),
          title: teaching[key].name,
          location: teaching[key].location,
          comments: teaching[key].description
        });
      }
    });
    sortRows(otherTeachingRows, "term");
    otherTeachingTable.bulkAddRowsFromData(otherTeachingRows, true);

    // Get data from CV
    const mentoring = cc?.newDef?.lists?.mentoring || {};
    const mentoringKeys = filterTimeframe(mentoring, timeframe);
    // Add to table
    mentoringKeys.forEach((key) => {
      const rowkey = menteeTable
        .addRowFromData({
          menteeName: mentoring[key].title,
          menteeType: mentoring[key].role,
          academicOutputs: mentoring[key].description,
          effort: getParsedEffort(mentoring[key].effort)
        })
        .split("-")
        .pop();
      if (Boolean(rowkey) && !isNaN(+rowkey) && isFinite(+rowkey)) {
        menteeTable.addHiddenData(+rowkey, "trueType", mentoring[key].type);
        menteeTable.addHiddenData(
          +rowkey,
          "uclaAffiliated",
          mentoring[key].uclaAffiliated
        );
      }
    });
  });
  hookEditHasher(menteeTable);
  hookEditHasher(otherTeachingTable);
  return [menteeTable, otherTeachingTable];
};

const teachingProfessorSection = (container: JQuery, cc: CCSetup) => {
  const graduateColumns = [
    { display: "Name", db: "name" as const, width: "30%" },
    { display: "Year PhD Granted", db: "year" as const, width: "15%" },
    {
      display: "Institutional Affiliation",
      db: "affiliation" as const,
      width: "30%"
    },
    { display: "Title", db: "title" as const, width: "25%" }
  ];
  const candidateColumns = [
    { display: "Name", db: "name" as const, width: "50%" },
    { display: "Year Advanced to Candidacy", db: "year" as const, width: "50%" }
  ];

  container.append(
    CCWrapper(
      "GRADUATES WHO HAVE COMPLETED Ph.D. (OR OTHER TERMINAL ADVANCED DEGREES) AT UC DURING THE PREVIOUS " +
        "FIVE-YEAR PERIOD UNDER PRINCIPAL SUPERVISION OF FACULTY MEMBER (THAT IS, FACULTY MEMBER WAS CHAIR " +
        "OF DOCTORAL COMMITTEE)",
      "",
      DefaultStyling.bold
    )
  );

  // adding this here inside the special professor section since it could not be included
  // in the structure_ucla_data_summary where it would show for all data summaries
  const numDuringPeriod = CCInput(
    "String",
    "Number during specified period",
    "num_during_period",
    "cc-optional"
  );
  container.append(numDuringPeriod);
  $('[data-key="num_during_period"]').on("input", () => {
    const value = String($('[data-key="num_during_period"]').val() || ""); // Grab value from input
    cc.newDef.career[cc.viewYear].data[dataPrefix + "num_during_period"] =
      value; // Set db
    cc.checkSave(); // Save
  });
  const valueFromDb =
    cc.newDef.career[cc.viewYear].data[dataPrefix + "num_during_period"];
  // If anything exists in db
  if (valueFromDb != undefined) {
    // Set that as the value
    // This is only called once on load
    $('[data-key="num_during_period"]').val(valueFromDb);
  }

  container.append(
    CCWrapper(
      "CURRENT STATUS OF THESE DEGREE HOLDERS (IF KNOWN)",
      "",
      DefaultStyling.bold
    )
  );

  const graduateTable = new CCInteractiveTable(
    container,
    cc,
    "teachingGraduate",
    dataPrefix,
    "custom-teaching-graduates",
    graduateColumns
  );

  container.append(
    CCWrapper(
      "GRADUATES ADVANCED TO CANDIDACY FOR THE Ph.D. (OR CANDIDATES FOR OTHER TERMINAL ADVANCED DEGREES) " +
        "CURRENTLY WORKING UNDER THE PRINCIPAL SUPERVISION OF FACULTY MEMBER (THAT IS, FACULTY MEMBER IS CHAIR " +
        "OF DOCTORAL COMMITTEE)",
      "",
      DefaultStyling.bold
    )
  );
  const candidateTable = new CCInteractiveTable(
    container,
    cc,
    "teachingCandidate",
    dataPrefix,
    "custom-teaching-candidates",
    candidateColumns
  );

  // Populate from db
  graduateTable.populateFromDatabase();
  candidateTable.populateFromDatabase();

  hookEditHasher(graduateTable);
  hookEditHasher(candidateTable);

  return [graduateTable, candidateTable];
};

const superviseSection = (container: JQuery, cc: CCSetup) => {
  // Define columns
  const tableColumns = [
    { display: "Name", db: "name" as const },
    { display: "Title", db: "title" as const },
    { display: "Institutional Affiliation", db: "institution" as const }
  ];
  // Add table
  const table = new CCInteractiveTable(
    container,
    cc,
    "supervise",
    dataPrefix,
    "custom-supervise",
    tableColumns
  );
  // Populate table
  table.populateFromDatabase();
  return table;
};

const committeeSection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(
      CCButton("primary", "committeeClear noPrint", "CLEAR ALL DATA") +
        CCButton("primary", "committeeLoad noPrint", "LOAD DATA FROM CV")
    )
  );
  // Column definitions
  const academicColumns = [
    { display: "Committee", db: "committee" as const, width: "20%" },
    { display: "Institution", db: "institution" as const, width: "20%" },
    { display: "Role", db: "role" as const },
    { display: "Effort", db: "effort" as const },
    { display: "DIV (Y/N)", db: "div" as const },
    { display: "Period of Service", db: "period" as const, width: "20%" }
  ];
  const communityColumns = [
    { display: "Description", db: "description" as const, width: "50%" },
    { display: "Effort", db: "effort" as const, width: "20%" },
    { display: "DIV (Y/N)", db: "div" as const, width: "10%" },
    { display: "Date(s)", db: "dates" as const, width: "20%" }
  ];
  const honorsColumns = [
    { display: "Description", db: "description" as const, width: "40%" },
    { display: "Organization", db: "org" as const, width: "40%" },
    { display: "Date(s)", db: "dates" as const, width: "20%" }
  ];
  const fellowshipColumns = [
    { display: "Source", db: "source" as const },
    { display: "Grant #", db: "grant" as const, width: "10%" },
    { display: "Title", db: "title" as const, width: "20%" },
    { display: "Role", db: "role" as const },
    { display: "(PI/Co-PI)(Name if you are not)", db: "picopi" as const },
    { display: "% Effort", db: "effort" as const, width: "5%" },
    { display: "Duration", db: "duration" as const },
    { display: "Direct Costs - your component only*", db: "costs" as const }
  ];

  // Headers and tables
  container.append(
    CCWrapper(CCHeader("h5", "", "Service on Academic Senate Committees"))
  );

  const academicTable = new CCInteractiveTable(
    container,
    cc,
    "academicCommittee",
    dataPrefix,
    "custom-committee-academic",
    academicColumns
  );

  container.append(CCWrapper(CCHeader("h5", "", "Other Committee Service")));

  const otherCommitteeTable = new CCInteractiveTable(
    container,
    cc,
    "otherCommittee",
    dataPrefix,
    "custom-committee-other",
    academicColumns
  );

  container.append(
    CCWrapper(
      CCHeader(
        "h5",
        "",
        "COMMUNITY SERVICE ACTIVITIES (e.g. providing service related to the improvement of elementary and secondary education)"
      )
    )
  );

  const communityTable = new CCInteractiveTable(
    container,
    cc,
    "communityService",
    dataPrefix,
    "custom-community",
    communityColumns
  );

  container.append(
    CCWrapper(CCHeader("h5", "", "HONORS AND SPECIAL RECOGNITION RECEIVED"))
  );
  container.append(
    CCWrapper(
      CCButton("primary", "honorsClear noPrint", "CLEAR ALL DATA") +
        CCButton("primary", "honorsLoad noPrint", "LOAD DATA FROM CV")
    )
  );

  const honorsTable = new CCInteractiveTable(
    container,
    cc,
    "honorsAndRecognition",
    dataPrefix,
    "custom-honors",
    honorsColumns
  );

  container.append(
    CCWrapper(CCHeader("h5", "", "FELLOWSHIP AND RESEARCH GRANTS RECEIVED"))
  );

  const fellowshipTable = new CCInteractiveTable(
    container,
    cc,
    "fellowshipAndResearch",
    dataPrefix,
    "custom-fellowship",
    fellowshipColumns,
    {
      addRowButton: true,
      widthOverride: "1350px",
      editable: {
        disableEverything: false,
        allowAddingRows: true,
        allowEditingText: true,
        allowRemovingRows: false
      }
    }
  );
  // Special case - we want to highlight the last column
  const fellowshipHead = $("#fellowshipAndResearchBody").siblings("thead");
  const columnToHighlight = fellowshipHead.children().eq(0).children().eq(-1);
  columnToHighlight.css("font-weight", "bold").css("color", "darkblue");
  // We also want to highlight the little note
  container.next().css("font-weight", "bold").css("color", "darkblue");

  const tables: CCInteractiveTable<any>[] = [
    academicTable,
    otherCommitteeTable,
    communityTable
  ];

  // Populate from database
  tables.forEach((table) => table.populateFromDatabase());
  honorsTable.populateFromDatabase();
  fellowshipTable.populateFromDatabase();

  // Buttons
  $(".committeeClear").on("click", () => {
    tables.forEach((table) => table.clear());
  });
  $(".honorsClear").on("click", () => {
    honorsTable.clear();
    fellowshipTable.clear();
  });

  $(".committeeLoad").on("click", () => {
    // Figure out how many years back we are pulling
    const timeframe = getTimeframeSelection();
    if (!checkTimeframeValidity(timeframe)) return;
    // Clear all tables
    tables.forEach((table) => table.clearUnedited());

    // Get data from CV
    const committee = cc.newDef.lists?.committeeService || {};
    const committeeKeys = filterTimeframe(
      committee,
      timeframe,
      (item) => item?.academicSenateCommittee == true
    );
    const academicRows: InferedRowType<typeof academicColumns>[] = [];
    committeeKeys.forEach((key) => {
      academicRows.push({
        committee: committee[key].title || "",
        institution: committee[key].institution,
        effort: getParsedEffort(committee[key].effort),
        role: parseRoles(committee[key].roles),
        div: committee[key].dei == true ? "Y" : "N",
        period: getPeriodFromDateObject(committee[key], ONGOING_TEXT)
      });
    });
    sortRows(academicRows, "period");
    academicTable.bulkAddRowsFromData(academicRows, true);
    academicTable.addPseudoColumn(/^div$/, "isDiv", (value: any) => {
      const normalized = String(value).toUpperCase().trim();
      return normalized === "Y" || normalized === "YES";
    });

    const otherKeys = filterTimeframe(
      committee,
      timeframe,
      (item) =>
        item?.academicSenateCommittee != true && item?.uclaAffiliated == true
    );
    const otherRows: InferedRowType<typeof academicColumns>[] = [];
    otherKeys.forEach((key) => {
      otherRows.push({
        committee: committee[key].title || "",
        institution: committee[key].institution,
        effort: getParsedEffort(committee[key].effort),
        role: parseRoles(committee[key].roles),
        div: committee[key].dei == true ? "Y" : "N",
        period: getPeriodFromDateObject(committee[key], ONGOING_TEXT)
      });
    });
    sortRows(otherRows, "period");
    otherCommitteeTable.bulkAddRowsFromData(otherRows, true);
    otherCommitteeTable.addPseudoColumn(/^div$/, "isDiv", (value: any) => {
      const normalized = String(value).toUpperCase().trim();
      return normalized === "Y" || normalized === "YES";
    });

    const community = cc.newDef.lists?.community || {};
    const communityKeys = filterTimeframe(community, timeframe);
    const commRows: InferedRowType<typeof communityColumns>[] = [];
    communityKeys.forEach((key) => {
      let description = community[key].role;
      if (community[key].organization) {
        description += ", " + community[key].organization;
      }
      commRows.push({
        description: description,
        effort: getParsedEffort(community[key].effort),
        div: community[key].dei == true ? "Y" : "N",
        dates: getPeriodFromDateObject(community[key], ONGOING_TEXT)
      });
    });
    sortRows(commRows, "dates");
    communityTable.bulkAddRowsFromData(commRows, true);
    communityTable.addPseudoColumn(/^div$/, "isDiv", (value: any) => {
      const normalized = String(value).toUpperCase().trim();
      return normalized === "Y" || normalized === "YES";
    });
  });

  $(".honorsLoad").on("click", () => {
    // Figure out how many years back we are pulling
    const timeframe = getTimeframeSelection();
    if (!checkTimeframeValidity(timeframe)) return;
    honorsTable.clearUnedited();
    fellowshipTable.clearUnedited();

    const honors = cc.newDef.lists?.honors || {};
    const honorKeys = filterTimeframe(
      honors,
      timeframe,
      (item) => item?.type == "honors"
    );
    const honorRows: InferedRowType<typeof honorsColumns>[] = [];
    honorKeys.forEach((key) => {
      const dateValue = getPeriodFromDateObject(honors[key], ONGOING_TEXT);
      honorRows.push({
        description: honors[key].title || "",
        org: honors[key].location,
        dates: dateValue.replace(/ - (Ongoing|Present)/, "")
      });
    });
    sortRows(honorRows, "dates");
    honorsTable.bulkAddRowsFromData(honorRows, true);

    const fellowship = cc.newDef.lists?.projects || {};
    const fellowshipKeys = filterTimeframe(fellowship, timeframe);
    const fellowRows: InferedRowType<typeof fellowshipColumns>[] = [];
    fellowshipKeys.forEach((key) => {
      fellowRows.push({
        source: fellowship[key].fundingSource,
        grant: fellowship[key].grantTypeAndNumber,
        title: fellowship[key].title,
        role: fellowship[key].role,
        picopi: fellowship[key].piName,
        effort: fellowship[key].workTime,
        duration: getPeriodFromDateObject(fellowship[key], ONGOING_TEXT),
        costs: fellowship[key].directCostsYourComponent
      });
    });
    sortRows(fellowRows, "duration");
    fellowshipTable.bulkAddRowsFromData(fellowRows, true);
    // Special case - listen for edits so we can take care of the combined column in the output
    fellowshipTable.addPseudoColumn(/^role$/, "isPrincipal", (value: any) => {
      return String(value).toUpperCase().trim() === "PRINCIPAL INVESTIGATOR";
    });
  });
  tables.forEach((table) => hookEditHasher(table));
  hookEditHasher(honorsTable);
  hookEditHasher(fellowshipTable);
  return [...tables, honorsTable, fellowshipTable];
};

const activitiesSection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(
      CCButton("primary", "activitiesClear noPrint", "CLEAR ALL DATA") +
        CCButton("primary", "activitiesLoad noPrint", "LOAD DATA FROM CV")
    )
  );
  // Column definitions
  const scholarlyColumns = [
    { display: "Role", db: "role" as const, width: "20%" },
    { display: "Society", db: "name" as const, width: "30%" },
    { display: "DIV (Y/N)", db: "div" as const, width: "10%" },
    { display: "Date", db: "date" as const, width: "20%" },
    { display: "Effort Since Last Review", db: "effort" as const, width: "20%" }
  ];
  const unclassifiedCommitteesColumns = [
    { display: "Committee", db: "committee" as const, width: "20%" },
    { display: "Institution", db: "institution" as const, width: "20%" },
    { display: "Role", db: "role" as const },
    { display: "Effort", db: "effort" as const },
    { display: "DIV (Y/N)", db: "div" as const },
    { display: "Period of Service", db: "period" as const, width: "20%" }
  ];
  const editorialColumns = [
    { display: "Role", db: "role" as const, width: "20%" },
    { display: "DIV (Y/N)", db: "div" as const, width: "10%" },
    { display: "Organization", db: "org" as const, width: "30%" },
    { display: "Date", db: "date" as const },
    { display: "Effort Since Last Review", db: "effort" as const }
  ];
  const consultingColumns = [
    { display: "Title/Position", db: "title" as const, width: "30%" },
    { display: "DIV (Y/N)", db: "div" as const, width: "10%" },
    { display: "Organization", db: "org" as const, width: "20%" },
    { display: "Date", db: "date" as const },
    { display: "Effort Since Last Review", db: "effort" as const }
  ];
  const otherColumns = [
    { display: "Type", db: "type" as const, width: "30%" },
    {
      display: "Description/Citation",
      db: "description" as const,
      width: "50%"
    },
    { display: "Date", db: "date" as const, width: "20%" }
  ];
  const presColumns = [
    {
      display: "Topic/Title",
      db: "description" as const,
      width: "50%"
    },
    {
      display: "Organization",
      db: "organization" as const,
      width: "20%"
    },
    {
      display: "Location",
      db: "location" as const,
      width: "20%"
    },
    { display: "Date", db: "date" as const }
  ];

  // Headers and tables
  container.append(
    CCWrapper(
      CCHeader(
        "h5",
        "",
        "ACTIVITIES IN SCHOLARLY AND PROFESSIONAL SOCIETIES (e.g., serving as a committee member or as an officer of a scholarly or professional organization, or providing professional services to such organizations)"
      )
    )
  );

  const scholarlyTable = new CCInteractiveTable(
    container,
    cc,
    "scholarlyActivities",
    dataPrefix,
    "custom-activities-scholarly",
    scholarlyColumns
  );

  container.append(CCWrapper(CCHeader("h5", "", "COMMITTEE SERVICE")));
  const unclassifiedCommitteesTable = new CCInteractiveTable(
    container,
    cc,
    "unclassifiedCommittees",
    dataPrefix,
    "custom-committee-unclassified",
    unclassifiedCommitteesColumns
  );

  container.append(
    CCWrapper(
      CCHeader(
        "h5",
        "",
        "EDITORIAL SERVICES TO SCHOLARLY PUBLICATIONS (e.g., serving as a reviewer or editor)"
      )
    )
  );

  const editorialTable = new CCInteractiveTable(
    container,
    cc,
    "editorialActivities",
    dataPrefix,
    "custom-activities-editorial",
    editorialColumns
  );

  container.append(
    CCWrapper(
      CCHeader(
        "h5",
        "",
        "CONSULTING ACTIVITIES (e.g., providing professional, managerial, or technical services to individual clients, commercial entities, non-profit organizations, and governmental agencies; testifying as an expert in legislative, administrative, and judicial proceedings)"
      )
    )
  );

  const consultingTable = new CCInteractiveTable(
    container,
    cc,
    "consultingActivities",
    dataPrefix,
    "custom-activities-consulting",
    consultingColumns
  );

  container.append(
    CCWrapper(
      CCHeader(
        "h5",
        "",
        "OTHER PROFESSIONAL ACTIVITIES (e.g., practicing a profession on a part time basis; presenting invited lectures or papers; participating in or accepting a commission for a musical, dramatic, dance, or other artistic activity, performance, or event)"
      )
    )
  );

  const presentationsMapping = {};
  for (const category of presentationCategories) {
    presentationsMapping[category] = {};
    for (const subcategory of presentationSubcategories) {
      container.append(
        CCWrapper(
          CCHeader(
            "h5",
            "",
            // subcategory is one of [Local, Regional, National, International]
            // category == Oral     --> Oral Presentations (subcategory)
            // category == Poster   --> Poster Presentations (subcategory)
            // category == Lectures --> Lectures (subcategory)
            `${category} ${category == "Lectures" ? "" : "Presentations "}(${subcategory})`
          )
        )
      );
      presentationsMapping[category][subcategory] = new CCInteractiveTable(
        container,
        cc,
        `${category.toLowerCase()}${subcategory}`,
        dataPrefix,
        `custom-${category.toLowerCase()}-${subcategory.toLowerCase()}`,
        presColumns
      );
      presentationsMapping[category][subcategory].populateFromDatabase();
    }
  }

  container.append(CCWrapper(CCHeader("h5", "", "Other")));

  const otherTable = new CCInteractiveTable(
    container,
    cc,
    "otherActivities",
    dataPrefix,
    "custom-activities-other",
    otherColumns
  );

  const tables: CCInteractiveTable<any>[] = [
    scholarlyTable,
    unclassifiedCommitteesTable,
    editorialTable,
    consultingTable,
    otherTable
  ];

  // Populate from database
  tables.forEach((table) => table.populateFromDatabase());

  // Buttons
  $(".activitiesClear").on("click", () => {
    tables.forEach((table) => table.clear());
    for (const category of Object.values(presentationsMapping)) {
      for (const table of Object.values(category)) {
        table.clear();
      }
    }
  });

  $(".activitiesLoad").on("click", () => {
    // Figure out how many years back we are pulling
    const timeframe = getTimeframeSelection();
    if (!checkTimeframeValidity(timeframe)) return;
    // Clear all tables first
    tables.forEach((table) => table.clearUnedited());
    for (const category of Object.values(presentationsMapping)) {
      for (const table of Object.values(category)) {
        table.clearUnedited();
      }
    }

    // Get data from CV

    const societies = cc.newDef.lists?.societies || {};
    const societyKeys = filterTimeframe(societies, timeframe);
    const socRows: InferedRowType<typeof scholarlyColumns>[] = [];
    societyKeys.forEach((key) => {
      socRows.push({
        role: parseRoles(societies[key].roles),
        name: societies[key].society,
        div: societies[key].dei == true ? "Y" : "N",
        date: getPeriodFromDateObject(societies[key], ONGOING_TEXT),
        effort: getParsedEffort(societies[key].effort)
      });
    });
    sortRows(socRows, "date");
    scholarlyTable.bulkAddRowsFromData(socRows, true);
    scholarlyTable.addPseudoColumn(/^div$/, "isDiv", (value: any) => {
      const normalized = String(value).toUpperCase().trim();
      return normalized === "Y" || normalized === "YES";
    });

    const committee = cc.newDef.lists?.committeeService || {};
    const committeeKeys = filterTimeframe(
      committee,
      timeframe,
      (item) =>
        item?.academicSenateCommittee != true && item?.uclaAffiliated != true
    );
    const unclassifiedCommitteesRows: InferedRowType<
      typeof unclassifiedCommitteesColumns
    >[] = [];
    committeeKeys.forEach((key) => {
      unclassifiedCommitteesRows.push({
        committee: committee[key].title || "",
        institution: committee[key].institution,
        effort: getParsedEffort(committee[key].effort),
        role: parseRoles(committee[key].roles),
        div: committee[key].dei == true ? "Y" : "N",
        period: getPeriodFromDateObject(committee[key], ONGOING_TEXT)
      });
    });
    sortRows(unclassifiedCommitteesRows, "period");
    unclassifiedCommitteesTable.bulkAddRowsFromData(
      unclassifiedCommitteesRows,
      true
    );
    unclassifiedCommitteesTable.addPseudoColumn(
      /^div$/,
      "isDiv",
      (value: any) => {
        const normalized = String(value).toUpperCase().trim();
        return normalized === "Y" || normalized === "YES";
      }
    );

    const editorial = cc.newDef.lists?.editorialActivities || {};
    const editorialKeys = filterTimeframe(editorial, timeframe);
    const editRows: InferedRowType<typeof editorialColumns>[] = [];
    editorialKeys.forEach((key) => {
      let effort = getParsedEffort(editorial[key].effort);
      if (Boolean(editorial[key].reviewPeriod)) {
        effort += `, ${editorial[key].reviewPeriod} manuscripts reviewed`;
      }
      editRows.push({
        role: editorial[key].role,
        div: editorial[key].dei == true ? "Y" : "N",
        org: editorial[key].organization,
        date: getPeriodFromDateObject(editorial[key], ONGOING_TEXT),
        effort
      });
    });
    sortRows(editRows, "date");
    editorialTable.bulkAddRowsFromData(editRows, true);
    editorialTable.addPseudoColumn(/^div$/, "isDiv", (value: any) => {
      const normalized = String(value).toUpperCase().trim();
      return normalized === "Y" || normalized === "YES";
    });

    const consulting = cc.newDef.lists?.otherPositions || {};
    const consultingKeys = filterTimeframe(consulting, timeframe);
    const consRows: InferedRowType<typeof consultingColumns>[] = [];
    consultingKeys.forEach((key) => {
      consRows.push({
        title: consulting[key].title,
        div: consulting[key].dei == true ? "Y" : "N",
        org: consulting[key].location,
        date: getPeriodFromDateObject(consulting[key], ONGOING_TEXT),
        effort: getParsedEffort(consulting[key].effort)
      });
    });
    sortRows(consRows, "date");
    consultingTable.bulkAddRowsFromData(consRows, true);
    consultingTable.addPseudoColumn(/^div$/, "isDiv", (value: any) => {
      const normalized = String(value).toUpperCase().trim();
      return normalized === "Y" || normalized === "YES";
    });

    // Get data from CV
    const presentations = cc?.newDef?.lists?.presentations || {};
    const presentationKeys = filterTimeframe(presentations, timeframe);
    // Add to table

    const presRows: {
      [P in (typeof presentationCategories)[number]]+?: {
        [P in (typeof presentationSubcategories)[number]]+?: InferedRowType<
          typeof presColumns
        >[];
      };
    } = {};
    presentationKeys.forEach((key) => {
      let categoryKey: (typeof presentationCategories)[number] =
        {
          Podium: "Oral",
          Poster: "Poster"
        }[presentations[key].presType] ?? "Lectures";
      const subcategoryKey: (typeof presentationSubcategories)[number] =
        presentations[key].type.charAt(0).toUpperCase() +
        presentations[key].type.substring(1);
      presRows[categoryKey] ??= {};
      presRows[categoryKey][subcategoryKey] ??= [];
      presRows[categoryKey][subcategoryKey].push({
        description: presentations[key].line,
        organization: presentations[key].organization,
        location: presentations[key].location,
        date: getPeriodFromDateObject(presentations[key], ONGOING_TEXT)
      });
    });
    for (const category of Object.keys(presRows)) {
      for (const subcategory of Object.keys(presRows[category])) {
        sortRows(presRows[category][subcategory], "date");
        presentationsMapping[category][subcategory].bulkAddRowsFromData(
          presRows[category][subcategory],
          true
        );
      }
    }
  });

  tables.forEach((table) => hookEditHasher(table));
  for (const category of Object.values(presentationsMapping)) {
    for (const table of Object.values(category)) {
      hookEditHasher(table);
    }
  }

  return tables;
};

export default {
  title: "UCLA Data Summary",
  tabTitle: "Data Summary",
  cvSetup: function ({
    contextMethods,
    contextConstants,
    contextState,
    conference,
    backend,
    professorVersion = false
  }) {
    let structure = new CCStructure(uclaDataSummaryStructure);
    if (professorVersion) {
      structure = new CCStructure(uclaDataSummaryStructureProfessor);
    }

    const setup = new CCSetup(
      conference,
      backend,
      dataPrefix,
      contextMethods,
      contextConstants,
      contextState
    );

    setup.addButton(
      "default",
      CCIcon("arrow-left"),
      "header",
      "careerYearPrev",
      () => setup.changeYear(-1)
    );
    setup.addHTML(
      CCText("h4", "careerYear", setup.period, DefaultStyling.careerYear),
      {
        relative: "after",
        selector: ".careerYearPrev"
      }
    );
    setup.addButton(
      "default",
      CCIcon("arrow-right"),
      "header",
      "careerYearNext",
      () => setup.changeYear(1)
    );
    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Date Limited CV</span>",
      "header",
      "downloadDateLimited",
      () => setup.dateLimitedCVExport(contextState.customExportSettings)
    );
    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Full CV</span>",
      "header",
      "downloadFull",
      () => setup.fullCVExport(contextState.customExportSettings)
    );
    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Data Summary</span>",
      "header",
      "downloadDataSummary",
      () => {
        // This function is called right before the output is created
        const data = setup.newDef.career[setup.viewYear].data;
        const dataOverride = {};
        if (data != undefined) {
          // Make sure there isn't a double % sign
          if (data[setup.dataPrefix + "custom-fellowship"]) {
            const keys = Object.keys(
              data[setup.dataPrefix + "custom-fellowship"]
            ).filter(
              (key) => data[setup.dataPrefix + "custom-fellowship"][key] != null
            );
            keys.forEach((key) => {
              const entry = data[setup.dataPrefix + "custom-fellowship"][key];
              const effort = String(entry.effort || "").trim();
              if (effort && effort.endsWith("%")) {
                data[setup.dataPrefix + "custom-fellowship"][key].effort =
                  effort.substring(0, effort.length - 1);
              }
            });
          }
          // Format this date to human readable (MMM = Shortened month)
          if (data[setup.dataPrefix + "appointment_date"]) {
            dataOverride["appointment_date"] = dayjs(
              data[setup.dataPrefix + "appointment_date"]
            ).format("MMM D, YYYY");
          }
        }

        const teachingMenteesInfo =
          data[setup.dataPrefix + "custom-teaching-mentees"];
        const menteesTypeMapping = {
          postdoc: "peer",
          undergraduate: "primary",
          students: "programs",
          graduate: "secondary"
        };
        for (const [key, val] of Object.entries(teachingMenteesInfo)) {
          if (!Boolean(key) || !Boolean(val)) continue;
          const type = menteesTypeMapping[val["trueType"]] ?? "programs";
          const tableKey = Boolean(val["uclaAffiliated"]) ? "teaching-" : "";
          dataOverride[`custom-${tableKey}mentees-${type}`] ??= {};
          dataOverride[`custom-${tableKey}mentees-${type}`][key] = val;
        }

        // Set first column to "None" if no data.
        if (
          !Boolean(data[setup.dataPrefix + "institution_prior"]) ||
          !Boolean(data[setup.dataPrefix + "title_prior"])
        ) {
          dataOverride["institution_prior"] = "None";
          dataOverride["title_prior"] = "";
          dataOverride["salary_prior"] = "";
        }
        if (!Boolean(data[setup.dataPrefix + "num_advanced"])) {
          dataOverride["num_advanced"] = "None";
        }
        if (!Boolean(data[setup.dataPrefix + "num_postdoc"])) {
          dataOverride["num_postdoc"] = "None";
        }

        for (const typeMapping of Object.values(menteesTypeMapping)) {
          if (
            Object.keys(
              dataOverride[`custom-teaching-mentees-${typeMapping}`] ?? {}
            ).length === 0
          ) {
            dataOverride[`custom-teaching-mentees-${typeMapping}`] = {
              [`custom-teaching-mentees-${typeMapping}-0`]: {
                menteeName: "None",
                menteeType: "",
                academicOutputs: "",
                currentPosition: ""
              }
            };
          }
        }

        const genericNoneableTables = {
          "custom-degrees": "degree",
          "custom-teaching-record": "term",
          "custom-teaching-record-no-course-num": "term",
          "custom-supervise": "name",
          "custom-committee-academic": "committee",
          "custom-committee-other": "committee",
          "custom-community": "description",
          "custom-honors": "description",
          "custom-fellowship": "source",
          "custom-activities-scholarly": "name",
          "custom-committee-unclassified": "committee",
          "custom-activities-editorial": "role",
          "custom-activities-consulting": "title",
          "custom-activities-other": "type"
        };
        for (const [key, value] of Object.entries(genericNoneableTables)) {
          if (
            Object.values(data[setup.dataPrefix + key] ?? {}).filter(Boolean)
              .length === 0
          ) {
            dataOverride[key] = {
              [`${key}-0`]: {
                [value]: "None"
              }
            };
          }
        }
        for (const category of presentationCategories) {
          for (const subcategory of presentationSubcategories) {
            const key = `custom-${category.toLowerCase()}-${subcategory.toLowerCase()}`;
            if (
              Object.values(data[setup.dataPrefix + key] ?? {}).filter(Boolean)
                .length === 0
            ) {
              dataOverride[key] = {
                [`${key}-0`]: {
                  description: "None"
                }
              };
            }
          }
        }
        if (professorVersion) {
          setup.conferenceExport(
            "ucla_data_summary_professor.docx",
            "UCLA Data Summary Professor",
            dataOverride,
            () => ""
          );
        } else {
          setup.conferenceExport(
            "ucla_data_summary_v1_1.docx",
            "UCLA Data Summary",
            dataOverride,
            () => "",
            {
              font: "Times New Roman",
              size: "10"
            }
          );
        }
      }
    );
    setup.addButton(
      "primary",
      CCIcon("print") + "<span>Print</span>",
      "header",
      "downloadPrint",
      () => setup.printExport("Title")
    );

    setup.generateFieldsFromStructure(structure, setup.ccRoot, false);
    setup.addHTML(
      CCWrapper(
        CCHeader(
          "h4",
          "",
          "Information for this form is pulled directly from your CV. Anything created, updated, or deleted on this form is a localized change and does NOT flow back to the original CV record. Please bring your CV up-to-date before completing this form."
        )
      ),
      {
        relative: "prepend",
        selector: "#printableContents"
      }
    );

    setup.defineCustomSection("custom-degrees", degreesSection, tableStatus);
    setup.defineCustomSection(
      "custom-supervise",
      superviseSection,
      tableStatus
    );
    setup.defineCustomSection(
      "custom-timeframe",
      timeframeSelectSection,
      tableStatus
    );
    setup.defineCustomSection(
      "custom-teaching-record",
      teachingRecordSection,
      tableStatus
    );
    setup.defineCustomSection(
      "custom-teaching-other",
      teachingOtherSection,
      tableStatus
    );

    if (professorVersion) {
      setup.defineCustomSection(
        "custom-teaching-professor",
        teachingProfessorSection,
        tableStatus
      );
    }

    setup.defineCustomSection(
      "custom-committee",
      committeeSection,
      tableStatus
    );
    setup.defineCustomSection(
      "custom-activities",
      activitiesSection,
      tableStatus
    );

    const lastPosition = getLastPosition(setup.newDef) || {};
    setup.populateReadonlyValues([
      { key: "faculty_name", value: setup.newDef?.bio?.name || "" },
      {
        key: "current_title",
        value: lastPosition.title || ""
      },
      {
        key: "appointment_date",
        value: getStringFromDateObject(lastPosition.time, "YYYY-MM-DD")
      }
    ]);

    let reviewNumber =
      setup.newDef.career[setup.viewYear]?.data[dataPrefix + "review_number"];
    if (reviewNumber == undefined) {
      const lastYearReviewNumber =
        setup.newDef.career[setup.viewYear - 1]?.data[
          dataPrefix + "review_number"
        ];
      if (
        lastYearReviewNumber != undefined &&
        !isNaN(parseInt(lastYearReviewNumber))
      ) {
        reviewNumber = parseInt(lastYearReviewNumber) + 1;
      }
    }
    setup.setValue("review_number", reviewNumber ?? 1);

    setup.update(setup.struct.structure);

    contextMethods.updateLayout();
  },
  manageSetup: function (
    settingsEl: JQuery,
    { conference, updateConfDate, saveConference, hierarchyNames },
    user: User
  ) {
    const ccManage = new CCManage(conference, saveConference, updateConfDate, {
      includeSections: {
        cutoff: true,
        mentees: true,
        goals: false,
        questions: false,
        mentors: true,
        reminders: false,
        years: true
      },
      sortBy: "mentor",
      verbage: {
        mentor: "Appraiser",
        mentee: "Mentee"
      }
    });
    ccManage.createSettingsElements(settingsEl);
    updateConfDate(conference);
    ccManage.createMentorsMenteesSection();
    ccManage.createAwesomplete();
    ccManage.hookMessageButton(user);
    ccManage.populateMenteesSection(hierarchyNames);
  }
};
